import { useLayoutEffect, useRef, type JSX } from "react";
import { Icon } from "../ui";
import { mimeToIcon } from "../../utils/mime";

export interface MessageComposerAttachment {
  readonly id: string;
  readonly name: string;
  readonly mimeType?: string;
}

export interface MessageComposerProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onSend?: () => void;
  readonly placeholder?: string;
  readonly rows?: number;
  readonly disabled?: boolean;
  readonly sendOnEnter?: boolean;
  /** "stacked" — textarea + toolbar below (default). "inline" — single-line input with icons on the sides. */
  readonly layout?: "stacked" | "inline";
  /** Icon name for the send button. Default: "arrow-up". */
  readonly sendIcon?: "arrow-up" | "send";
  /** Tailwind class for the send icon color (e.g. "text-[#6AB2F2]"). */
  readonly sendIconClassName?: string;
  /**
   * When true, the attach (paperclip) button is NOT rendered.
   * Per DEC-3 / INV-11: telegram mode has no attachment plumbing.
   */
  readonly hideAttach?: boolean;
  /**
   * When provided, the paperclip becomes interactive and fires this on click.
   * Shell stays mode-agnostic: wrapper decides what clicking means.
   */
  readonly onAttachClick?: () => void;
  /** Attachment chips rendered above the textarea (stacked) or above the inline row. */
  readonly attachments?: readonly MessageComposerAttachment[];
  /** Fired when the chip × is clicked. */
  readonly onRemoveAttachment?: (id: string) => void;
  /** Optional error text rendered below the composer (e.g. upload failure). */
  readonly errorText?: string;
  /**
   * Optional `data-testid` applied to the underlying `<textarea>`.
   * Used by wrappers (`TelegramReplyComposer`, `EmailReplyComposer`) so E2E
   * specs can locate the composer input without coupling to class names.
   */
  readonly textareaTestId?: string;
}

function AttachmentChips({
  attachments,
  onRemoveAttachment,
}: {
  readonly attachments: readonly MessageComposerAttachment[];
  readonly onRemoveAttachment?: (id: string) => void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap gap-1.5 px-2 py-1.5">
      {attachments.map((a) => (
        <span
          key={a.id}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface-hover text-xs text-content"
          data-testid="composer-attachment-chip"
          data-attachment-id={a.id}
        >
          <Icon name={mimeToIcon(a.mimeType ?? "")} size={12} className="text-content-secondary" />
          <span className="max-w-[160px] truncate">{a.name}</span>
          {onRemoveAttachment ? (
            <button
              type="button"
              onClick={() => { onRemoveAttachment(a.id); }}
              className="w-4 h-4 rounded-full flex items-center justify-center bg-transparent border-none cursor-pointer hover:bg-surface"
              title="Remove attachment"
              aria-label={`Remove ${a.name}`}
            >
              <Icon name="close" size={10} className="text-content-secondary" />
            </button>
          ) : null}
        </span>
      ))}
    </div>
  );
}

export function MessageComposer({
  value,
  onChange,
  onSend,
  placeholder = "Type a message...",
  rows = 3,
  disabled = false,
  sendOnEnter = true,
  layout = "stacked",
  sendIcon = "arrow-up",
  sendIconClassName,
  hideAttach = false,
  onAttachClick,
  attachments,
  onRemoveAttachment,
  errorText,
  textareaTestId,
}: MessageComposerProps): JSX.Element {
  const canSend = !disabled && value.trim().length > 0 && onSend !== undefined;
  const inlineRef = useRef<HTMLTextAreaElement | null>(null);
  const attachInteractive = !hideAttach && onAttachClick !== undefined;
  const hasChips = attachments !== undefined && attachments.length > 0;

  useLayoutEffect(() => {
    if (layout !== "inline" || !inlineRef.current) return;
    const el = inlineRef.current;
    el.style.height = "auto";
    el.style.height = `${String(Math.min(el.scrollHeight, 200))}px`;
  }, [value, layout]);

  if (layout === "inline") {
    return (
      <div className="flex-1 flex flex-col">
        {hasChips ? (
          <AttachmentChips attachments={attachments} onRemoveAttachment={onRemoveAttachment} />
        ) : null}
        <div className="flex items-end gap-2 bg-transparent border border-edge rounded-md min-h-12 px-2 py-1.5">
          {!hideAttach && (
            <button
              type="button"
              onClick={attachInteractive ? onAttachClick : undefined}
              disabled={!attachInteractive}
              className="w-8 h-8 rounded-md flex items-center justify-center bg-transparent border-none cursor-pointer hover:bg-surface-hover shrink-0 disabled:cursor-default"
              title="Attach"
            >
              <Icon name="paperclip" size={18} className="text-content-secondary" />
            </button>
          )}
          <textarea
            ref={inlineRef}
            data-testid={textareaTestId}
            value={value}
            onChange={(e) => { onChange(e.target.value); }}
            onKeyDown={(e) => {
              if (sendOnEnter && e.key === "Enter" && !e.shiftKey && canSend) {
                e.preventDefault();
                onSend();
              }
            }}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            className="bg-transparent border-none outline-none text-content text-sm flex-1 resize-none self-center py-1.5 placeholder:text-content-tertiary leading-5 max-h-[200px] overflow-y-auto"
          />
          <button
            type="button"
            className="w-8 h-8 rounded-md flex items-center justify-center bg-transparent border-none cursor-pointer hover:bg-surface-hover shrink-0"
            title="Emoji"
          >
            <Icon name="smile" size={18} className="text-content-secondary" />
          </button>
          <button
            type="button"
            onClick={onSend}
            disabled={!canSend}
            className="w-8 h-8 rounded-md flex items-center justify-center bg-transparent hover:bg-surface-hover border-none cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
            title="Send"
          >
            <Icon name={sendIcon} size={18} className={sendIconClassName ?? "text-content"} />
          </button>
        </div>
        {errorText ? (
          <div className="px-2 pt-1 text-xs text-red-500" role="alert">{errorText}</div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col">
      <div className="flex-1 flex flex-col bg-transparent border border-edge rounded-md overflow-hidden">
        {hasChips ? (
          <AttachmentChips attachments={attachments} onRemoveAttachment={onRemoveAttachment} />
        ) : null}
        <textarea
          data-testid={textareaTestId}
          value={value}
          onChange={(e) => { onChange(e.target.value); }}
          onKeyDown={(e) => {
            if (sendOnEnter && e.key === "Enter" && !e.shiftKey && canSend) {
              e.preventDefault();
              onSend();
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          className="bg-transparent border-none outline-none text-content text-sm resize-none px-3 py-2 placeholder:text-content-tertiary"
        />
        <div className="flex items-center gap-1 px-2 py-1">
          {!hideAttach && (
            <button
              type="button"
              onClick={attachInteractive ? onAttachClick : undefined}
              disabled={!attachInteractive}
              className="w-8 h-8 rounded-md flex items-center justify-center bg-transparent border-none cursor-pointer hover:bg-surface-hover disabled:cursor-default"
              title="Attach"
            >
              <Icon name="paperclip" size={18} className="text-content-secondary" />
            </button>
          )}
          <button
            type="button"
            className="w-8 h-8 rounded-md flex items-center justify-center bg-transparent border-none cursor-pointer hover:bg-surface-hover"
            title="Emoji"
          >
            <Icon name="smile" size={18} className="text-content-secondary" />
          </button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={onSend}
            disabled={!canSend}
            className="h-8 w-8 rounded-md flex items-center justify-center bg-transparent hover:bg-surface-hover border-none cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            title="Send"
          >
            <Icon name={sendIcon} size={18} className={sendIconClassName ?? "text-content"} />
          </button>
        </div>
      </div>
      {errorText ? (
        <div className="px-2 pt-1 text-xs text-red-500" role="alert">{errorText}</div>
      ) : null}
    </div>
  );
}
